import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Button, Card, ConfigProvider, DatePicker, Divider, Flex, Form, Input, message, Modal, Popconfirm, Radio, Select, Spin, Table} from "antd";
import dayjs from "dayjs";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import GroupService from "../../../../../services/GroupService";
import {LocalName} from "../../../../../utils/LocalName";
import {CloseOutlined, DownloadOutlined, EyeOutlined, FormOutlined} from "@ant-design/icons";
import FloatActionMenu from "../../../../../components/UI/FloatActionMenu/FloatActionMenu";
import MyText from "../../../../../components/UI/Text/MyText";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {clrs} from "../../../../../constants/colors";
import UserService from "../../../../../services/UserService";
import classes from "../../Courses/CreateCourse/CreateCourse.module.css";
import FormItem from "antd/es/form/FormItem";
import ActCompletionWorkService from "../../../../../services/ActCompletionWorkService";
import {useTranslation} from "react-i18next";
import debounce from "lodash/debounce";
import {useDispatch, useSelector} from "react-redux";
import {getAllJournals} from "../../../../../store/slices/JournalSlice";
import {changeCurrentPage} from "../../../../../store/slices/tableController/JournalsListTableController";
import MyTable from "../../../../../components/Table/MyTable";

const GroupsList = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {journals, isLoading, hasMore, error} = useSelector(state => state.journalList)
    const {currentPage, pageSize} = useSelector(state => state.journalsListTableController);
    const categoriesList = useSelector(state => state.categoriesList);

    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [resettingNumbering, setResettingNumbering] = useState(false); // п.1
    // ТЗ updated п.5: вкладка Активные/Архив групп
    const [archived, setArchived] = useState(false);

    const [teachers, setTeachers] = useState([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);

    const [isLoadingViewStudents, setIsLoadingViewStudents] = useState(false);
    const [openViewStudentsModal, setOpenViewStudentsModal] = useState(false);
    const [viewStudents, setViewStudents] = useState(null);

    const [isLoadingViewTeacher, setIsLoadingViewTeacher] = useState(false);
    const [openViewTeacherModal, setOpenViewTeacherModal] = useState(false);
    const [viewTeacher, setViewTeacher] = useState(null);

    const [openViewTeacherOrderModal, setOpenViewTeacherOrderModal] = useState(false);

    const [viewGroupId, setViewGroupId] = useState(null)

    const [openReplaceTeacherModal, setOpenReplaceTeacherModal] = useState(false)

    const [form] = Form.useForm();
    const teacher = Form.useWatch('teacher', form);
    const selectedStudents = Form.useWatch('students', form);

    const teacherContractNumber = Form.useWatch('teacherContractNumber', form);
    const teacherContractDate = Form.useWatch('teacherContractDate', form);

    const [students, setStudents] = useState([]);
    const [openAddStudentsModal, setOpenAddStudentsModal] = useState(false)

    // п.6/п.7: даты присоединения учеников открытой группы (studentId -> ISO date)
    const [groupEnrollments, setGroupEnrollments] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        dispatch(getAllJournals({
            page: currentPage,
            limit: pageSize,
            categoryId: selectedCategoryId,
            archived
        }));
        fetchStudents();
    }, []);

    useEffect(() => {
        dispatch(getAllJournals({
            page: 1,
            limit: pageSize,
            categoryId: selectedCategoryId,
            archived
        }));
    }, [selectedCategoryId, archived]);

    const fetchData = async (params) => {
        return dispatch(getAllJournals({
            ...params,
            categoryId: selectedCategoryId,
            archived
        }));
    }

    // ТЗ updated п.5: убрать группу в архив / вернуть из архива
    async function handleSetGroupArchived(groupId, value) {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await GroupService.setGroupArchived(groupId, value)
            .then(() => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);
                dispatch(getAllJournals({page: currentPage, limit: pageSize, categoryId: selectedCategoryId, archived}));
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            });
    }

    // ТЗ updated п.5: разово убрать в архив все группы выбранной категории
    async function handleArchiveByCategory() {
        if (!selectedCategoryId) {
            message.warning(<IntlMessage id={'groups.archiveCategoryPickFirst'}/>, 5);
            return;
        }
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await GroupService.archiveGroupsByCategory(selectedCategoryId, true)
            .then((r) => {
                message.destroy();
                message.success(`✔ ${r.data}`, 5);
                dispatch(getAllJournals({page: 1, limit: pageSize, categoryId: selectedCategoryId, archived}));
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            });
    }

    const saveCurrentPageSettings = (page, pageSize) => {
        dispatch(changeCurrentPage({page: page, pageSize: pageSize}));
    };

    const columns = [
        {
            title: <IntlMessage id={'name2'}/>,
            render: (_, record) => (
                <p className={classes.copyLinkBtn}
                   onClick={() => navigate(`/p/group/${record?.id}`)}>{LocalName.getName(record.name)}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'number'}/>,
            render: (_, record) => (
                <p>{record.number}</p>
            ),
            width: '5%',
        },
        {
            title: <IntlMessage id={'ordersPage.teacher'}/>,
            render: (_, record) => (
                <Flex justify={"space-between"} align={"center"}>
                    <p>{record.teacherFullName}</p>
                    <Flex gap={"small"}>
                        <Button onClick={() => getTeacher(record.id, record.teacherId)}>
                            <EyeOutlined/>
                        </Button>
                        <Button onClick={() => {
                            setOpenViewTeacherOrderModal(true);
                            setViewGroupId(record.id)
                        }}>
                            <FormOutlined/>
                        </Button>
                    </Flex>
                </Flex>
            ),
            width: '15%',
        },
        {
            title: <IntlMessage id={'teacherContractColumn'}/>,
            render: (_, record) => (
                <Flex justify={"flex-start"} align={"flex-start"} vertical gap={5}>
                    <p>{record.teacherContractDate}</p>
                    <p>{record.teacherContractNumber}</p>
                </Flex>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'ordersPage.students'}/>,
            render: (_, record) => (
                <Flex justify={"space-between"} align={"center"}>
                    <p>{record.numberOfStudents}</p>
                    <Button onClick={() => getGroupStudents(record.id)}>
                        <EyeOutlined/>
                    </Button>
                </Flex>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'ordersPage.academicYear'}/>,
            render: (_, record) => (
                <p>{record.academicYear}</p>
            ),
            width: '5%',
        },
        {
            title: <IntlMessage id={'ordersPage.duration'}/>,
            render: (_, record) => (
                <p>{record?.duration}</p>
            ),
            width: '5%',
        },
        {
            title: <IntlMessage id={'ordersPage.schedule'}/>,
            render: (_, record) => (
                <p style={{whiteSpace: 'pre-line'}}>{record?.schedule || '—'}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'category'}/>,
            render: (_, record) => (
                <p>{LocalName.getName(record.category)}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'conductedLessons'}/>,
            render: (_, record) => (
                <p>{record.conductedLessons} / {record.totalLessons}</p>
            ),
            width: '5%',
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) =>
                <>
                    <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                        {/*<p onClick={() => downloadDocumentACW(record.id)} className={classes.copyLinkBtn}><IntlMessage id={'acw.acw'}/></p>*/}
                        {
                            !!record?.teacherContractDate &&
                            !!record?.teacherContractNumber &&
                            (
                                ['General English', 'Mathematics'].includes(record?.category?.nameEng) ?
                                record?.conductedLessons === record?.lessons?.length && record?.isAllGraded && !record?.isStillNotASPed
                                    :
                                    true
                            ) &&
                            (
                                record?.group?.category?.nameEng === "Foundation" ?
                                    record?.isStillNotASPed
                                    :
                                    true
                            ) &&
                            <p onClick={() => toGenerateAWR(record?.id)} className={classes.copyLinkBtn}>
                                <IntlMessage id={'acw.aas'}/></p>
                        }
                        {
                            record?.isStillNotASPed === false && ['General English', 'Mathematics'].includes(record?.category?.nameEng) &&
                            <p>
                                <IntlMessage id={'acw.aasHas'}/>
                            </p>
                        }
                        {/* ТЗ updated п.5: архив вместо удаления — данные группы сохраняются */}
                        <Popconfirm
                            title={archived ? <IntlMessage id={'groups.unarchive'}/> : <IntlMessage id={'groups.archive'}/>}
                            description={<IntlMessage id={'groups.archiveConfirm'}/>}
                            onConfirm={() => handleSetGroupArchived(record.id, !archived)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <p className={classes.copyLinkBtn}>
                                {archived ? <IntlMessage id={'groups.unarchive'}/> : <IntlMessage id={'groups.archive'}/>}
                            </p>
                        </Popconfirm>
                        <Popconfirm
                            // icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                            title={t('delete')}
                            description={t('doYouWantDelete')}
                            onConfirm={() => {
                                deleteGroup(record.id)
                            }}
                            // className={classes.deleteBlock}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <p className={classes.copyLinkBtn}><IntlMessage id={'delete'}/></p>
                        </Popconfirm>
                    </Flex>

                </>,
            width: '3%',
        },
    ];

    async function toGenerateAWR(id) {
        await ActCompletionWorkService.generateAcwForGroup(id)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
            })
    }

    async function downloadExcelGroupsTeachersData() {
        message.loading(<IntlMessage id={'loading'}/>);
        await GroupService.downloadTeachersDataExcel()
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "teachers.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    async function downloadExcelGroupsTeachersAccountData() {
        message.loading(<IntlMessage id={'loading'}/>);
        await GroupService.downloadTeachersAccountExcel()
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "teachers-accounter.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    async function downloadExcelGroupsTeachersACW() {
        message.loading(<IntlMessage id={'loading'}/>);
        await GroupService.downloadTeachersACW()
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "teachers-acw.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    async function downloadExcelFoundationAccounter() {
        message.loading(<IntlMessage id={'loading'}/>);
        await GroupService.downloadTeachersFoundation()
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "foundation-teachers.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    async function downloadExcelGroupsStudentsData() {
        message.loading(<IntlMessage id={'loading'}/>);
        await GroupService.downloadExcelGroupsStudentsData()
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "teachers.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async function deleteGroup(id) {
        await GroupService.deleteGroupById(id)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
            })
    }

    async function removeStudentFromGroup(groupId, studentId) {
        await GroupService.removeStudentFromGroup(groupId, studentId)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
                getGroupStudents(groupId);
            })
    }

    async function replaceGroupTeacher() {
        await GroupService.replaceGroupTeacher(viewGroupId, teacher)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
                form.resetFields();
                setOpenReplaceTeacherModal(false)
                setOpenViewTeacherModal(false)
            })
    }

    async function addTeacherOrder() {
        await GroupService.updateTeacherOrder(viewGroupId, teacherContractNumber, teacherContractDate)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
                form.resetFields();
                setOpenViewTeacherOrderModal(false)
            })
    }

    async function addStudentsToGroup() {
        await GroupService.addStudents(viewGroupId, selectedStudents)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllJournals({
                    page: currentPage,
                    limit: pageSize,
                    categoryId: selectedCategoryId
                }));
                form.resetFields();
                setOpenAddStudentsModal(false);
                getGroupStudents(viewGroupId);
            })
    }

    async function getTeacher(groupId, id) {
        setViewGroupId(groupId)
        setIsLoadingViewTeacher(true);
        setOpenViewTeacherModal(true);
        setViewTeacher(null);
        await GroupService.getTeacherByID(id)
            .then((r) => {
                setViewTeacher(r.data);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
                setOpenViewTeacherModal(false);
            })
            .finally(() => {
                setIsLoadingViewTeacher(false);
            })
    }

    async function updateJoinDate(studentId, dateStr) {
        await GroupService.setEnrollment(viewGroupId, studentId, dateStr)
            .then(() => {
                setGroupEnrollments(prev => ({...prev, [studentId]: dateStr}));
                message.success(<IntlMessage id={'success'}/>, 3);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            });
    }

    async function getGroupStudents(groupId) {
        setViewGroupId(groupId)
        setIsLoadingViewStudents(true);
        setOpenViewStudentsModal(true);
        setViewStudents(null);
        // п.6/п.7: подгружаем даты присоединения учеников группы
        GroupService.getGroupEnrollments(groupId)
            .then((d) => {
                const map = {};
                (d?.enrollments || []).forEach(e => {
                    if (e.joinDate) map[e.studentId] = e.joinDate;
                });
                setGroupEnrollments(map);
            })
            .catch(() => setGroupEnrollments({}));
        await GroupService.getGroupStudents(groupId)
            .then((r) => {
                setViewStudents(r.data);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
                setOpenViewStudentsModal(false);
            })
            .finally(() => {
                setIsLoadingViewStudents(false);
            })
    }

    const fetchTeachers = async () => {
        await UserService.getTeachers(1, 100)
            .then((r) => {
                setTeachers(r.data.map(teacher => {
                    return {
                        value: teacher.id,
                        label: teacher.fullName
                    }
                }));
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {

            })
    }

    const fetchStudents = async () => {
        await UserService.getStudents(1, 100)
            .then((r) => {
                setStudents(r.data.map(student => {
                    return {
                        value: student.id,
                        label: student.fullName
                    }
                }));
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {

            })
    }


    const fetchStudentSearch = debounce(async (searchText) => {
        setIsLoadingStudents(true);
        try {
            const response = await UserService.getStudentsSearchable(1, 100, searchText); // предполагаем, что 3й параметр — строка поиска
            const options = response.data.map((student) => ({
                value: student.id,
                label: student.fullName,
            }));
            setStudents(options);
        } catch (e) {
            message.error(<IntlMessage id="studentsFetchError"/>);
        } finally {
            setIsLoadingStudents(false);
        }
    }, 500); // 500ms задержка

    const studentsColumns = [
        {
            title: <IntlMessage id={'fullName'}/>,
            render: (_, record) => (
                <p>{record.fullName}</p>
            ),
            width: '50%'
        },
        {
            title: <IntlMessage id={'email'}/>,
            render: (_, record) => (
                <p>{record.email}</p>
            ),
            width: '30%'
        },
        {
            // п.6/п.7: дата присоединения ученика (редактируется админом)
            title: <IntlMessage id={'groups.joinDate'}/>,
            render: (_, record) => (
                <DatePicker
                    size="small"
                    format="DD.MM.YYYY"
                    value={groupEnrollments[record.id] ? dayjs(groupEnrollments[record.id]) : null}
                    onChange={(d) => d && updateJoinDate(record.id, d.format('YYYY-MM-DD'))}
                />
            ),
            width: '30%'
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) => (
                <Popconfirm
                    // icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                    title={t('removeStudents')}
                    description={t('doYouWantDelete')}
                    onConfirm={() => {
                        removeStudentFromGroup(viewGroupId, record.id)
                    }}
                    // className={classes.deleteBlock}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <Button>
                        <CloseOutlined style={{color: clrs.whiterRed}}/>
                    </Button>
                </Popconfirm>
            ),
            width: '50%'
        },
    ];

    // п.1: сброс нумерации групп «по кнопке» — перевод учебного года на следующий.
    // Если выбрана категория в фильтре — сбрасываем только её, иначе все.
    async function resetGroupNumbering() {
        setResettingNumbering(true);
        message.loading(<IntlMessage id={'loading'}/>, 0);
        try {
            await GroupService.resetGroupNumbering(selectedCategoryId || undefined);
            message.destroy();
            message.success(<IntlMessage id={'success'}/>, 5);
        } catch (e) {
            message.destroy();
            message.error(<IntlMessage id={'error'}/>, 5);
        } finally {
            setResettingNumbering(false);
        }
    }

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} vertical gap={20}>
            {/* п.5.1: плавающее дублирование действий выгрузки */}
            <FloatActionMenu actions={[
                {key: 'teachers', label: t('ordersPage.teachers'), icon: <DownloadOutlined/>, onClick: downloadExcelGroupsTeachersData},
                {key: 'students', label: t('ordersPage.students'), icon: <DownloadOutlined/>, onClick: downloadExcelGroupsStudentsData},
                {key: 'accounter', label: t('excelForAccounter'), icon: <DownloadOutlined/>, onClick: downloadExcelGroupsTeachersAccountData},
                {key: 'acts', label: t('excelForActs'), icon: <DownloadOutlined/>, onClick: downloadExcelGroupsTeachersACW},
                {key: 'foundation', label: t('foundationPage.foundation'), icon: <DownloadOutlined/>, onClick: downloadExcelFoundationAccounter},
            ]}/>
            <Flex align={"center"} gap={"middle"}>
                <GreyButton onClick={downloadExcelGroupsTeachersData} backgroundColor={"#36b400"}
                            fontColor={clrs.black}><IntlMessage id={'ordersPage.teachers'}/></GreyButton>
                <GreyButton onClick={downloadExcelGroupsStudentsData} backgroundColor={"#36b400"}
                            fontColor={clrs.black}><IntlMessage id={'ordersPage.students'}/></GreyButton>
                <GreyButton onClick={downloadExcelGroupsTeachersAccountData} backgroundColor={"#36b400"}
                            fontColor={clrs.black}><IntlMessage id={'excelForAccounter'}/></GreyButton>
                <GreyButton onClick={downloadExcelGroupsTeachersACW} backgroundColor={"#36b400"}
                            fontColor={clrs.black}><IntlMessage id={'excelForActs'}/></GreyButton>
                <GreyButton onClick={downloadExcelFoundationAccounter} backgroundColor={"#36b400"}
                            fontColor={clrs.black}><IntlMessage id={'foundationPage.foundation'}/></GreyButton>
                {/* п.1: сброс нумерации групп (новый учебный год) */}
                <Popconfirm
                    title={<IntlMessage id={'groups.resetNumbering'}/>}
                    description={<IntlMessage id={selectedCategoryId ? 'groups.resetNumberingConfirm' : 'groups.resetNumberingConfirmAll'}/>}
                    onConfirm={resetGroupNumbering}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <GreyButton loading={resettingNumbering} backgroundColor={clrs.whiterRed} fontColor={clrs.white}>
                        <IntlMessage id={'groups.resetNumbering'}/>
                    </GreyButton>
                </Popconfirm>
                {/* ТЗ updated п.5: вкладка Активные/Архив */}
                <Radio.Group value={archived} onChange={(e) => setArchived(e.target.value)} optionType="button" buttonStyle="solid">
                    <Radio.Button value={false}><IntlMessage id={'contracts.active'}/></Radio.Button>
                    <Radio.Button value={true}><IntlMessage id={'contracts.archiveTab'}/></Radio.Button>
                </Radio.Group>
                {/* ТЗ updated п.5: разово убрать в архив все группы выбранной категории */}
                {
                    !archived &&
                    <Popconfirm
                        title={<IntlMessage id={'groups.archiveCategory'}/>}
                        description={<IntlMessage id={'groups.archiveCategoryConfirm'}/>}
                        onConfirm={handleArchiveByCategory}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <GreyButton backgroundColor={clrs.orange} fontColor={clrs.black}>
                            <IntlMessage id={'groups.archiveCategory'}/>
                        </GreyButton>
                    </Popconfirm>
                }
            </Flex>
            <Card style={{width: "100%", borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"}>
                    {/*<Divider orientation="left" style={{fontWeight: 600}}>*/}
                    {/*    <IntlMessage id={'filter'}/>*/}
                    {/*</Divider>*/}
                    <Radio.Group
                        value={selectedCategoryId}
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                        <Radio value={null}>
                            <IntlMessage id={'allCategories'}/>
                        </Radio>
                        {
                            categoriesList?.categories?.map((category) => (
                                <Radio value={category.id} key={category.id}>
                                    {LocalName.getName(category)}
                                </Radio>
                            ))
                        }
                    </Radio.Group>
                </Flex>
            </Card>
            <Form form={form} className={classes.form}>
                <MyTable
                    isLoading={isLoading}
                    dataSource={journals}
                    columns={columns}
                    fetchData={fetchData}
                    saveCurrentPage={saveCurrentPageSettings}
                    initialPage={currentPage}
                    initialPageSize={pageSize}
                    hasMore={hasMore}
                />
                <Modal
                    width={800}
                    maskClosable={true}
                    title={<IntlMessage id={'ordersPage.students'}/>}
                    open={openViewStudentsModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    style={{
                        top: 20,
                    }}
                    onOk={() => {
                        setOpenViewStudentsModal(false)
                    }}
                    onCancel={() => {
                        setOpenViewStudentsModal(false)
                    }}
                    footer={[
                        <Flex align={"flex-end"} justify={"flex-end"} gap={"middle"}>
                            <Button key="submit" type="primary" onClick={() => {
                                setOpenAddStudentsModal(true)
                            }}>
                                <IntlMessage id={"addStudents"}/>
                            </Button>
                            <Button key="submit" type="primary" onClick={() => {
                                setOpenViewStudentsModal(false)
                            }}>
                                OK
                            </Button>
                        </Flex>
                    ]}
                >
                    {
                        isLoadingViewStudents ? (
                            <Flex justify="center" align="center" style={{minHeight: 200}}>
                                <Spin size="large"/>
                            </Flex>
                        ) : (
                            !!viewStudents &&
                            <ConfigProvider
                                renderEmpty={() => <div style={{textAlign: 'center'}}><IntlMessage id={"noData"}/></div>}
                            >
                                <Table
                                    dataSource={viewStudents}
                                    columns={studentsColumns}
                                    pagination={{defaultPageSize: 5}}
                                />
                            </ConfigProvider>
                        )
                    }
                </Modal>
                <Modal
                    width={800}
                    maskClosable={true}
                    title={<IntlMessage id={'ordersPage.teacher'}/>}
                    open={openViewTeacherModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    style={{
                        top: 20,
                    }}
                    onOk={() => {
                        setOpenViewTeacherModal(false)
                    }}
                    onCancel={() => {
                        setOpenViewTeacherModal(false)
                    }}
                    footer={[
                        <Flex align={"flex-end"} justify={"flex-end"} gap={"middle"}>
                            <Button key="submit" type="primary" onClick={() => {
                                fetchTeachers();
                                setOpenReplaceTeacherModal(true);
                            }}>
                                <IntlMessage id={"replaceTeacher"}/>
                            </Button>
                            <Button key="submit" type="primary" onClick={() => {
                                setOpenViewTeacherModal(false)
                            }}>
                                OK
                            </Button>
                        </Flex>

                    ]}
                >
                    {
                        isLoadingViewTeacher ? (
                            <Flex justify="center" align="center" style={{minHeight: 200}}>
                                <Spin size="large"/>
                            </Flex>
                        ) : (
                            openViewTeacherModal && viewTeacher &&
                            <Flex style={{width: "100%", position: "relative"}} vertical gap={10}>
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'firstName'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.firstName
                                        }
                                    </MyText>
                                </Flex>
                                {viewTeacher?.middleName &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'middleName'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.middleName
                                        }
                                    </MyText>
                                </Flex>
                            }
                            <Flex gap={5} wrap={"wrap"}>
                                <MyText strong size={"small"}>
                                    <IntlMessage id={'lastName'}/>
                                </MyText>
                                <span>:</span>
                                <MyText size={"small"}>
                                    {
                                        viewTeacher?.lastName
                                    }
                                </MyText>
                            </Flex>
                            {viewTeacher?.birthDay &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'birthDay'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.birthDay
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.address &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'address'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.address
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.telNumber &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'telNumber'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.telNumber
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.email &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'email'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.email
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.iin &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'iin'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.iin
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.idCardNumber &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'idCardNumber'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.idCardNumber
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.idCardFromWhom &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'idCardFromWhom'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.idCardFromWhom
                                        }
                                    </MyText>
                                </Flex>
                            }
                            {viewTeacher?.idCardDate &&
                                <Flex gap={5} wrap={"wrap"}>
                                    <MyText strong size={"small"}>
                                        <IntlMessage id={'idCardDate'}/>
                                    </MyText>
                                    <span>:</span>
                                    <MyText size={"small"}>
                                        {
                                            viewTeacher?.idCardDate
                                        }
                                    </MyText>
                                </Flex>
                                }
                            </Flex>
                        )
                    }
                </Modal>
                <Modal
                    width={400}
                    maskClosable={true}
                    title={<IntlMessage id={'chooseTeacher'}/>}
                    open={openReplaceTeacherModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    onOk={() => {
                        setOpenReplaceTeacherModal(false)
                    }}
                    onCancel={() => {
                        setOpenReplaceTeacherModal(false)
                    }}
                    footer={[
                        <Button key="submit" type="primary" onClick={() => replaceGroupTeacher()}>
                            <IntlMessage id={"replace"}/>
                        </Button>
                    ]}
                >
                    {
                        openReplaceTeacherModal &&
                        <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                  className={classes.formInputBlock} name={"teacher"}
                        >
                            <Select options={teachers}
                                    placeholder={<IntlMessage id={'ordersPage.teacher'}/>}
                            />
                        </FormItem>
                    }
                </Modal>
                <Modal
                    width={400}
                    maskClosable={true}
                    title={<IntlMessage id={'teacherContractNumber'}/>}
                    open={openViewTeacherOrderModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    onOk={() => {
                        setOpenViewTeacherOrderModal(false)
                    }}
                    onCancel={() => {
                        setOpenViewTeacherOrderModal(false)
                    }}
                    footer={[
                        <Button key="submit" type="primary" onClick={addTeacherOrder}>
                            <IntlMessage id={"change"}/>
                        </Button>
                    ]}
                >
                    {
                        openViewTeacherOrderModal &&
                        <Flex style={{width: "100%", position: "relative"}} vertical gap={10}>
                            <FormItem rules={[{
                                required: openViewTeacherOrderModal,
                                message: <IntlMessage id={'requiredField'}/>,
                            },
                            ]} label={<IntlMessage id={'teacherContractNumber'}/>} labelCol={{span: 24}}
                                      className={classes.formInputBlock}
                                      name="teacherContractNumber">
                                <Input/>
                            </FormItem>
                            <FormItem rules={[{
                                required: openViewTeacherOrderModal,
                                message: <IntlMessage id={'requiredField'}/>,
                            },
                            ]} label={<IntlMessage id={'teacherContractDate'}/>} labelCol={{span: 24}}
                                      className={classes.formInputBlock}
                                      name="teacherContractDate">
                                <Input type={'date'} className={classes.formInput} style={{maxWidth: 200}}/>
                            </FormItem>
                        </Flex>
                    }
                </Modal>
                <Modal
                    width={400}
                    maskClosable={true}
                    title={<IntlMessage id={'addStudents'}/>}
                    open={openAddStudentsModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    onOk={() => {
                        setOpenAddStudentsModal(false)
                    }}
                    onCancel={() => {
                        setOpenAddStudentsModal(false)
                    }}
                    footer={[
                        <Button key="submit" type="primary" onClick={() => addStudentsToGroup()}>
                            <IntlMessage id={"addStudents"}/>
                        </Button>
                    ]}
                >
                    {
                        openAddStudentsModal &&
                        <FormItem rules={[{required: false, message: <IntlMessage id={'requiredField'}/>}]}
                                  className={classes.formInputBlock} name={"students"}
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                filterOption={false}
                                onSearch={fetchStudentSearch}
                                loading={isLoadingStudents}
                                notFoundContent={isLoadingStudents ? <Spin size="small"/> : null}
                                placeholder={<IntlMessage id="ordersPage.students"/>}
                                options={students}
                            />
                        </FormItem>
                    }
                </Modal>
            </Form>
        </Flex>
    );
};

export default GroupsList;